import React, { useEffect } from 'react';

import { useCurrentUserId } from 'src/gql/hooks/user';
import { useSnapshot } from 'valtio';

import {
  $bulkExportTasks,
  hydrateBulkExportTasks,
  removeBulkExportTask,
} from './bulk-export-tasks';
import { BulkExportTaskPanel } from './BulkExportTaskPanel';

/**
 * Mounted once, app-wide, inside the background-task tray. Renders a panel for
 * each active bulk export so they stack with uploads and other jobs. Kept
 * separate from the page components that start an export so a panel survives the
 * dialog closing, navigation between tabs, and a full page refresh (the tracked
 * ids are persisted per user in `bulk-export-tasks`).
 */
export function BulkExportTaskPanels(): JSX.Element | null {
  const userId = useCurrentUserId();
  const { exportIds, userId: storeUserId } = useSnapshot($bulkExportTasks);

  useEffect(() => {
    if (userId) hydrateBulkExportTasks(userId);
  }, [userId]);

  // Wait until the store is hydrated for the current user before rendering, so a
  // sign-in on a shared browser can't briefly flash the previous user's panels.
  if (!userId || storeUserId !== userId) return null;

  return (
    <>
      {exportIds.map(exportId => (
        <BulkExportTaskPanel
          key={exportId}
          exportId={exportId}
          onDismiss={() => removeBulkExportTask(exportId)}
        />
      ))}
    </>
  );
}
